import type {
  ErrorEnvelope,
  Health,
  KioskCurrentResponse,
  PhotoMissingReason,
  PhotoMissingSubmission,
  PunchRequest,
  PunchResult,
  PunchSyncResult,
} from '@worklogix/shared';
import { ApiError, createApiClient } from '@worklogix/web-common';

import { API_BASE } from './config';
import { clearDeviceToken, DEVICE_HEADER, getDeviceToken } from './device/deviceToken';

/**
 * Kiosk API layer.
 *
 * DEVICE-TOKEN AUTH, NOT A BEARER: every authenticated call carries the paired
 * device token in the `x-worklogix-device` header (Dev-B's KioskAuthGuard reads
 * it). There is NO `Authorization: Bearer` header and NO MSAL/Entra anywhere.
 *
 * INTEGRATION NOTE (top item): web-common's frozen `createApiClient` only sets
 * `Authorization: Bearer <token>` and exposes no custom-header hook, so it cannot
 * carry `x-worklogix-device`. We therefore (a) reuse `createApiClient` for the
 * unauthenticated `GET /health` skew probe, and (b) reuse its `ApiError` type for
 * consistent error semantics, but implement device-authed requests with the thin
 * `deviceRequest` below. If web-common later grows a header hook, these can fold
 * back into it. web-common is out of this dev's lane and was not modified.
 */

/** Unauthenticated client, reused solely for the public `GET /health` probe. */
const healthClient = createApiClient({
  baseUrl: API_BASE,
  getToken: () => Promise.resolve(''),
});

function joinUrl(path: string): string {
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

async function parseErrorEnvelope(response: Response): Promise<ErrorEnvelope['error'] | null> {
  try {
    const data = (await response.json()) as Partial<ErrorEnvelope>;
    if (data.error && typeof data.error.code === 'string') {
      return data.error;
    }
  } catch {
    // Non-JSON / empty body — fall through to a generic error.
  }
  return null;
}

interface DeviceRequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Raw, non-JSON request body (e.g. a JPEG `Blob`). When set, it is sent as-is
   * with {@link DeviceRequestOptions.contentType} and `body` is ignored. Used by
   * the binary photo upload.
   */
  rawBody?: BodyInit;
  /** Explicit `Content-Type` for {@link DeviceRequestOptions.rawBody}. */
  contentType?: string;
  /**
   * Explicit device token, used ONLY by the pairing screen to validate a pasted
   * token before persisting it. Omitted for normal calls, which read the paired
   * token from storage.
   */
  token?: string;
  /**
   * When true, a 401 does NOT count toward un-pairing and never clears the token
   * — it just throws so the caller can retry. Used by the best-effort photo drain,
   * which is documented as unable to affect the clock: a photo upload must never
   * be able to un-pair the device.
   */
  suppressUnpair?: boolean;
}

/**
 * Consecutive stored-token 401s before the device is un-paired. The server never
 * 401s a VALID token (verified), so a lone 401 is treated as transient (a blip /
 * a request that momentarily carried no token) and the pairing is kept; only a
 * genuine rotate/revoke — which 401s every request — trips this threshold. Any
 * successful stored-token response resets the counter.
 */
const UNPAIR_AFTER_CONSECUTIVE_401 = 3;
let consecutiveAuthFailures = 0;

/**
 * Device-authenticated request. Attaches `x-worklogix-device: <token>`, throws
 * {@link ApiError} on non-2xx, and on 401 clears the stored token so the app
 * falls back to the pairing screen (token revoked/rotated at the server).
 *
 * A 401 only clears storage when the request used the STORED token — validating
 * a pasted token during pairing must not wipe an existing pairing.
 */
async function deviceRequest<T>(path: string, opts: DeviceRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = opts;

  const usingStoredToken = opts.token === undefined;
  const token = opts.token ?? getDeviceToken();
  if (token === null) {
    throw new ApiError(401, 'DEVICE_NOT_PAIRED', 'This device is not paired.');
  }

  const headers = new Headers();
  headers.set(DEVICE_HEADER, token);

  const init: RequestInit = { method, headers };
  if (opts.rawBody !== undefined) {
    // Binary payload (JPEG): send as-is under the caller's content-type.
    headers.set('Content-Type', opts.contentType ?? 'application/octet-stream');
    init.body = opts.rawBody;
  } else if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    init.signal = signal;
  }

  const response = await fetch(joinUrl(path), init);

  if (response.status === 401) {
    // A stored-token 401 counts toward un-pairing UNLESS suppressed (photo drain).
    // Only drop to pairing after several CONSECUTIVE failures — a genuine
    // rotate/revoke 401s every request, whereas a lone 401 is transient and must
    // not force a manual re-pair. A pairing probe (explicit token) never counts.
    if (usingStoredToken && opts.suppressUnpair !== true) {
      consecutiveAuthFailures += 1;
      if (consecutiveAuthFailures >= UNPAIR_AFTER_CONSECUTIVE_401) {
        consecutiveAuthFailures = 0;
        clearDeviceToken();
      }
    }
    throw new ApiError(401, 'DEVICE_UNAUTHORIZED', 'Device authorization was rejected.');
  }

  // Any successful stored-token response proves the pairing is good — reset the
  // consecutive-failure counter so isolated 401s never accumulate to an un-pair.
  if (usingStoredToken && response.ok) {
    consecutiveAuthFailures = 0;
  }

  if (!response.ok) {
    const envelope = await parseErrorEnvelope(response);
    throw new ApiError(
      response.status,
      envelope?.code ?? 'HTTP_ERROR',
      envelope?.message ?? `Request failed with status ${response.status}.`,
      envelope?.requestId
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Liveness probe — used to measure clock skew on load. Unauthenticated. */
export function getHealth(): Promise<Health> {
  return healthClient.getHealth();
}

/** Open sessions + department identity for this device's department. */
export function getCurrent(signal?: AbortSignal): Promise<KioskCurrentResponse> {
  return deviceRequest<KioskCurrentResponse>('/kiosk/current', signal ? { signal } : {});
}

/**
 * Validate a pasted device token during pairing by calling `GET /kiosk/current`
 * with it explicitly. On success the token is good (and we get the department
 * context to cache); a 401 means the token was rejected. Does not touch storage.
 */
export function verifyDeviceToken(token: string): Promise<KioskCurrentResponse> {
  return deviceRequest<KioskCurrentResponse>('/kiosk/current', { token });
}

/** Online fast-path single punch. The offline-first flow uses {@link syncPunches}. */
export function punch(request: PunchRequest, signal?: AbortSignal): Promise<PunchResult> {
  return deviceRequest<PunchResult>('/kiosk/punch', {
    method: 'POST',
    body: request,
    ...(signal ? { signal } : {}),
  });
}

/**
 * Drain the durable queue: POST an ordered batch of punches. The server dedupes
 * by idempotency key and is authoritative on the result of each.
 */
export function syncPunches(
  requests: PunchRequest[],
  signal?: AbortSignal
): Promise<PunchSyncResult[]> {
  return deviceRequest<PunchSyncResult[]>('/kiosk/punch/sync', {
    method: 'POST',
    body: requests,
    ...(signal ? { signal } : {}),
  });
}

/**
 * Server acknowledgement for a photo upload (Dev-B). Only the HTTP status matters
 * to the drain (2xx = done, 409 = punch not yet synced, other = transient); this
 * shape is best-effort and tolerant of an empty/opaque body.
 */
export interface PhotoUploadAck {
  status?: 'stored' | 'already_stored' | 'missing';
}

function photoPath(idempotencyKey: string): string {
  return `/kiosk/punch/${encodeURIComponent(idempotencyKey)}/photo`;
}

/**
 * Upload a captured JPEG for a punch (SECOND drain, after the punch itself
 * synced). Binary `image/jpeg` body. A 409 (punch not yet on the server) surfaces
 * as an {@link ApiError} the drain retries after the next punch drain; a 2xx marks
 * the photo synced. Idempotent server-side — a replay is a no-op.
 *
 * DEPENDENCY (Dev-B): endpoint `POST /kiosk/punch/:idempotencyKey/photo`, device-
 * token auth, 409 on punch-not-found.
 */
export function uploadStoredPhoto(
  idempotencyKey: string,
  blob: Blob,
  signal?: AbortSignal
): Promise<PhotoUploadAck> {
  return deviceRequest<PhotoUploadAck>(photoPath(idempotencyKey), {
    method: 'POST',
    rawBody: blob,
    contentType: 'image/jpeg',
    // Best-effort drain: a photo 401 must never un-pair the device.
    suppressUnpair: true,
    ...(signal ? { signal } : {}),
  });
}

/**
 * Record that NO photo was captured for a punch (camera unavailable / denied /
 * failed). JSON `{ status:'missing', reason }` — never bytes. Same 409/2xx
 * semantics as {@link uploadStoredPhoto}.
 */
export function uploadMissingPhoto(
  idempotencyKey: string,
  reason: PhotoMissingReason,
  signal?: AbortSignal
): Promise<PhotoUploadAck> {
  const submission: PhotoMissingSubmission = { status: 'missing', reason };
  return deviceRequest<PhotoUploadAck>(photoPath(idempotencyKey), {
    method: 'POST',
    body: submission,
    // Best-effort drain: a photo 401 must never un-pair the device.
    suppressUnpair: true,
    ...(signal ? { signal } : {}),
  });
}
